import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Centralized asset loader for 3D models and potentially other assets.
 * Handles caching and asynchronous loading.
 */
class AssetLoader {
  constructor() {
    this.loader = new GLTFLoader();
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Load a GLTF/GLB model and cache it.
   * @param {string} url - path to the model file
   * @returns {Promise<THREE.Group>} - a promise that resolves to the loaded model group
   */
  async loadModel(url) {
    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    const promise = new Promise((resolve, reject) => {
      this.loader.load(
        url,
        (gltf) => {
          this.cache.set(url, gltf);
          resolve(gltf);
        },
        undefined,
        (error) => {
          console.error(`Error loading model at ${url}:`, error);
          reject(error);
        }
      );
    });

    this.loadingPromises.set(url, promise);
    return promise;
  }

  /**
   * Get a clone of a pre-loaded model.
   * @param {string} url - path to the model file
   * @returns {{scene: THREE.Group, animations: THREE.AnimationClip[]}|null}
   */
  cloneModel(url) {
    const gltf = this.cache.get(url);
    if (!gltf) return null;
    return {
      scene: gltf.scene.clone(),
      animations: gltf.animations
    };
  }

  /**
   * Preload a list of models.
   * @param {string[]} urls - array of model paths
   */
  async preloadModels(urls) {
    const uniqueUrls = [...new Set(urls)].filter(url => !!url);
    await Promise.all(uniqueUrls.map(url => this.loadModel(url)));
  }
}

export const assetLoader = new AssetLoader();
